import { myFirebase } from '@/config/firebaseConfig';
import { error } from 'console';
import 'firebase/compat/auth';
import 'firebase/compat/firestore';
import firebase from 'firebase/compat/app';
import { collection, query, where, onSnapshot, orderBy, getDoc, doc, Unsubscribe } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';

import {
  ReportInfo,
  Session,
  ReportInfoDoc,
  FileInfoDict,
  FileInfo,
  ReportInfoDict,
  ReportStatus,
  SessionCreationData,
} from '@/models';

// TODO: Add Archive session
class SessionManager {
  private auth = myFirebase.auth();
  private firestore = myFirebase.firestore();
  private reportInfoCache: ReportInfoDict = {};
  private fileInfoCache: FileInfoDict = {};

  // TODO: Change this to function?
  public getSessions = (userUID: string, clientID: string, callback: (sessions: Session[], error?: Error) => void) => {
    // console.log('Getting sessions for user:', userUID, 'and client:', clientID);

    const sessionsRef = collection(this.firestore, 'sessions');
    const q = query(
      sessionsRef,
      where('therapistId', '==', userUID),
      where('status', '==', 'Active'),
      where('clientId', '==', clientID),
      orderBy('date', 'desc'),
    );

    return onSnapshot(
      q,
      async (querySnapshot) => {
        try {
          const sessions = querySnapshot.docs.map((doc) => ({
            id: doc.id,
            ...doc.data(),
          })) as Session[];

          // Get all report IDs that aren't already in cache
          const uncachedReportIds = new Set<string>();
          sessions.forEach((session) => {
            if (session.reports && Array.isArray(session.reports)) {
              session.reports.forEach((reportId) => {
                if (!this.reportInfoCache[reportId]) {
                  uncachedReportIds.add(reportId);
                }
              });
            }
          });

          if (uncachedReportIds.size === 0) {
            // If all reports are cached, return immediately
            const enrichedSessions = this.enrichSessionsWithCache(sessions, this.reportInfoCache);
            callback(enrichedSessions);
            return;
          }

          // Batch fetch uncached report info
          const userRef = doc(this.firestore, 'users', userUID);
          const reportInfosRef = collection(userRef, 'reportInfo');

          const BATCH_SIZE = 10;
          const reportIdArray = Array.from(uncachedReportIds);
          const uncachedFileIds = new Set<string>();

          // Fetch reports in batches
          for (let i = 0; i < reportIdArray.length; i += BATCH_SIZE) {
            const batch = reportIdArray.slice(i, i + BATCH_SIZE);
            const batchQueries = batch.map((id) => getDoc(doc(reportInfosRef, id)));
            const batchSnapshots = await Promise.all(batchQueries);

            batchSnapshots.forEach((snap, index) => {
              if (snap.exists()) {
                const reportDoc = { id: snap.id, ...snap.data() } as ReportInfoDoc;
                if (reportDoc.status !== ReportStatus.DELETED && !this.fileInfoCache[reportDoc.fileId]) {
                  uncachedFileIds.add(reportDoc.fileId);
                }
              }
            });
          }

          // Batch fetch uncached file info
          if (uncachedFileIds.size > 0) {
            const filesRef = collection(userRef, 'kinematicsFiles');
            const fileIdArray = Array.from(uncachedFileIds);

            for (let i = 0; i < fileIdArray.length; i += BATCH_SIZE) {
              const batch = fileIdArray.slice(i, i + BATCH_SIZE);
              const batchQueries = batch.map((id) => getDoc(doc(filesRef, id)));
              const batchSnapshots = await Promise.all(batchQueries);

              batchSnapshots.forEach((snap, index) => {
                if (snap.exists()) {
                  const fileInfo = { id: snap.id, ...snap.data() } as FileInfo;
                  this.fileInfoCache[snap.id] = fileInfo;
                }
              });
            }
          }

          // After fetching all necessary data, update report cache
          const reportQueries = Array.from(uncachedReportIds).map((id) => getDoc(doc(reportInfosRef, id)));
          const reportSnapshots = await Promise.all(reportQueries);

          reportSnapshots.forEach((snap) => {
            if (snap.exists()) {
              const reportDoc = { id: snap.id, ...snap.data() } as ReportInfoDoc;
              const fileInfo = this.fileInfoCache[reportDoc.fileId];

              if (fileInfo && reportDoc.status !== ReportStatus.DELETED) {
                this.reportInfoCache[reportDoc.id] = {
                  ...reportDoc,
                  tags: fileInfo.tags,
                  filename: fileInfo.filename,
                  reportName: fileInfo.reportName,
                  uploadTimestamp: new Date(fileInfo.uploadTimestamp * 1000),
                };
              }
            }
          });

          const enrichedSessions = this.enrichSessionsWithCache(sessions, this.reportInfoCache);
          callback(enrichedSessions);
        } catch (error) {
          console.error('Error fetching report info:', error);
          callback([], error instanceof Error ? error : new Error('Unknown error occurred'));
        }
      },
      (error) => {
        // This is the onSnapshot error callback
        console.error('Snapshot listener error:', error);
        callback([], error);
      },
    );
  };

  private enrichSessionsWithCache = (sessions: Session[], reportCache: ReportInfoDict): Session[] => {
    return sessions.map((session) => ({
      ...session,
      reportInfo: session.reports ? session.reports.map((reportId) => reportCache[reportId]).filter(Boolean) : [],
    }));
  };

  public async createSession(sessionData: SessionCreationData): Promise<string> {
    try {
      const sessionRef = this.firestore.collection('sessions').doc();
      // TODO: change the date to admin.firestore.Timestamp.fromDate(new Date(date))
      const session = {
        ...sessionData,
        id: sessionRef.id,
        status: 'Active',
        _mutationHistory: [
          {
            mutationType: 'create',
            mutationDate: firebase.firestore.Timestamp.fromDate(new Date()),
            mutationBy: sessionData.therapistId,
          },
        ],
      };

      await sessionRef.set(session);
      return sessionRef.id;
    } catch (error) {
      console.error('Error creating session:', error);
      throw error;
    }
  }
}

// eslint-disable-next-line import/no-default-export
export default new SessionManager();
